/**
 * Cached cardholder-terminology lookups.
 *
 * The word a library's UI uses for its end users (patrons / customers /
 * members / users) is set once at onboarding (backend:
 * internal/service/library_terminology.go) and read very often across
 * COS-native single-tenant surfaces — so this module fetches it ONCE per
 * id per session (the circulate-products.ts module-singleton precedent,
 * extended to a per-id cache since a session can touch several
 * libraries' pages) rather than once per component mount.
 *
 * Two id spaces, because the backend serves both: legacy client_id (the
 * Library-settings surfaces) and COS partner_id (Nurture, Comms Hub, CID,
 * Preserve, Reserve, Lend — everything keyed by a partner, not a legacy
 * client). A failed fetch resolves to the default "customers" term rather
 * than rejecting — a copy nicety must never break a page — and that
 * resolved default is itself cached, so a library with no reachable
 * setting doesn't get re-fetched on every call.
 */
import { api } from "./api.js";
import { legacyClientId } from "./legacy-client.js";
import { cardholderTerm, setActiveCardholderTerm, type CardholderTerm } from "../utils/terminology.js";
import { CARDHOLDER_TERM_CHANGED_EVENT } from "../library/client-event.js";

interface CardholderTerminologyResponse {
	term?: string;
	/** The library's country, riding the same answer: it sets the date order. */
	country?: string;
	/** The library's date order ("mdy" or "dmy"): its own setting, else the country's. */
	date_order?: string;
}

// What one library lookup yields: the word, and the country the date box
// reads typed dates by. One request answers both.
interface LibraryLookup {
	term: CardholderTerm;
	country: string;
	/** '' when the server did not say (an older answer): the country decides then. */
	dateOrder: '' | 'mdy' | 'dmy';
}

function readDateOrder(raw: string | undefined): '' | 'mdy' | 'dmy' {
	return raw === 'mdy' || raw === 'dmy' ? raw : '';
}

const cache = new Map<string, Promise<LibraryLookup>>();

function lookup(key: string, fetchTerm: () => Promise<unknown>): Promise<LibraryLookup> {
	let entry = cache.get(key);
	if (!entry) {
		// Promise.resolve().then(...) so a SYNCHRONOUS throw (e.g. a test's
		// partial api mock missing the method) degrades to the default like
		// any other failure instead of blowing up the caller's lifecycle.
		entry = Promise.resolve()
			.then(fetchTerm)
			.then((res) => {
				const r = res as CardholderTerminologyResponse | undefined;
				return { term: cardholderTerm({ cardholder_terminology: r?.term }), country: r?.country ?? '', dateOrder: readDateOrder(r?.date_order) };
			})
			.catch(() => ({ term: cardholderTerm(undefined), country: '', dateOrder: '' as const }));
		cache.set(key, entry);
	}
	return entry;
}

function resolve(key: string, fetchTerm: () => Promise<unknown>): Promise<CardholderTerm> {
	return lookup(key, fetchTerm).then((l) => l.term);
}

/** Resolves via the legacy client_id — the Library-settings id space. */
export function cardholderTermForClient(clientId: number): Promise<CardholderTerm> {
	return resolve(`client:${clientId}`, () => api.getLibraryCardholderTerminology(clientId));
}

/** Resolves via a COS partner_id — every other single-tenant surface's id space. */
export function cardholderTermForPartner(partnerId: number): Promise<CardholderTerm> {
	return resolve(`partner:${partnerId}`, () => api.getLibraryCardholderTerminologyForPartner(partnerId));
}

/**
 * Resolves for the CALLER's own tenant with no id at all — the fallback for
 * a COS partner_id-space surface (Nurture, Comms Hub, CID) where a scoped
 * library caller has no local partner_id/client_id in JS scope to pass (the
 * server infers their tenant from claims). One shared cache entry per
 * session: a Communico staffer with no partner/client picked yet gets the
 * same 422-to-default resolution every such surface already falls back to,
 * so re-fetching per caller buys nothing.
 */
export function cardholderTermForSelf(): Promise<CardholderTerm> {
	return resolve("self", () => api.getLibraryCardholderTerminologySelf());
}

/**
 * Resolves for the library currently selected in the shared Partner-nav
 * picker (the legacy client id space `legacyClientId()` reads). This is the
 * right call for any single-tenant surface that has no partner_id/client_id
 * of its own in scope (Preserve, Lend, dashboard tiles, ...). Falls back to
 * cardholderTermForSelf() when nothing is picked (0), which still resolves
 * correctly for a library's own user via their claims-inferred tenant; a
 * Communico staffer with nothing picked gets the same default as
 * cardholderTermForSelf() always has.
 */
export function cardholderTermForCurrentLibrary(): Promise<CardholderTerm> {
	const clientId = legacyClientId();
	return clientId > 0 ? cardholderTermForClient(clientId) : cardholderTermForSelf();
}

/**
 * Resolves the word, the country AND the date order for the library selected in the shared
 * picker, from the one cached request the word already costs. The app root
 * calls this when the library changes: it makes the word active and sets the
 * date order from the country. Never rejects: an unreachable answer is the
 * default word and an empty country, which reads month first.
 */
export function libraryLookupForCurrentLibrary(): Promise<LibraryLookup> {
	const clientId = legacyClientId();
	return clientId > 0
		? lookup(`client:${clientId}`, () => api.getLibraryCardholderTerminology(clientId))
		: lookup('self', () => api.getLibraryCardholderTerminologySelf());
}

/**
 * Records a word the Library page just saved, so screens already open and
 * later lookups use it without a reload. Every cached answer is dropped (a
 * partner_id or "self" entry may name this same library under another id),
 * the saved word is primed for its client id and becomes the active one, and
 * CARDHOLDER_TERM_CHANGED_EVENT tells mounted controllers and the app root to
 * redraw.
 */
export function rememberSavedCardholderTerm(clientId: number, term: CardholderTerm): void {
	// Keep the country and date order the library already answered with: a word
	// change must not flip its date order back to the default.
	const prior = (clientId > 0 ? cache.get(`client:${clientId}`) : undefined) ?? cache.get('self');
	cache.clear();
	if (clientId > 0) {
		cache.set(
			`client:${clientId}`,
			(prior ?? Promise.resolve<LibraryLookup>({ term, country: '', dateOrder: '' })).then((p) => ({
				...p,
				term,
			})),
		);
	}
	setActiveCardholderTerm(term);
	window.dispatchEvent(new CustomEvent(CARDHOLDER_TERM_CHANGED_EVENT));
}

/** Test-only: clears the session cache so a case can assert a fresh fetch. */
export function _resetCardholderTermCacheForTests(): void {
	cache.clear();
}

/**
 * The country of the library selected in the shared picker (a code or a
 * name, as the library stores it), from the one cached request the word and
 * the date order already cost. '' when unknown. Never rejects. Screens that
 * word an address by country (ZIP code or Postal code) read this.
 */
export function libraryCountryForCurrentLibrary(): Promise<string> {
	return libraryLookupForCurrentLibrary().then((l) => l.country);
}
